import { useState, useEffect } from 'react'
import { GovUKButton } from '@/components/govuk/GovUKButton'
import { GovUKInput } from '@/components/govuk/GovUKInput'
import { GovUKTypography } from '@/components/govuk/GovUKTypography'
import { GovUKFormGroup } from '@/components/govuk/GovUKFormGroup'
import { GovUKTag } from '@/components/govuk/GovUKTag'
import { GovUKCheckbox } from '@/components/govuk/GovUKCheckbox'
import { PageContainer } from '@/components/PageContainer'
import { PageHeader } from '@/components/PageHeader'
import { SimplePiano } from './SimplePiano'
import styles from './PianoNoteTester.module.css'

// C Major scale notes (no flats)
const C_MAJOR_NOTES = ['C', 'D', 'E', 'F', 'G', 'A', 'B']

type GameState = 'setup' | 'playing' | 'won' | 'lost' | 'finished'

interface Game {
  timerDuration: number
  totalRounds: number
  state: GameState
  targetNote: string
  timeRemaining: number
  score: number
  currentRound: number
  showNotes: boolean
}

export function PianoNoteTester() {
  const [game, setGame] = useState<Game>({
    timerDuration: 10,
    totalRounds: 5,
    state: 'setup',
    targetNote: '',
    timeRemaining: 0,
    score: 0,
    currentRound: 0,
    showNotes: false,
  })

  const pickRandomNote = () => {
    const randomIndex = Math.floor(Math.random() * C_MAJOR_NOTES.length)
    return C_MAJOR_NOTES[randomIndex]
  }

  const startGame = () => {
    const note = pickRandomNote()
    setGame((prev) => ({
      ...prev,
      targetNote: note,
      timeRemaining: prev.timerDuration,
      score: 0,
      currentRound: 1,
      state: 'playing',
    }))
  }

  const resetGame = () => {
    setGame((prev) => ({
      ...prev,
      state: 'setup',
      targetNote: '',
      timeRemaining: 0,
      score: 0,
      currentRound: 0,
    }))
  }

  const nextRound = () => {
    if (game.currentRound < game.totalRounds) {
      const note = pickRandomNote()
      setGame((prev) => ({
        ...prev,
        targetNote: note,
        timeRemaining: prev.timerDuration,
        currentRound: prev.currentRound + 1,
        state: 'playing',
      }))
    } else {
      setGame((prev) => ({ ...prev, state: 'finished' }))
    }
  }

  const onPlayNote = (note: string) => {
    if (game.state !== 'playing') return

    if (note === game.targetNote) {
      setGame((prev) => ({
        ...prev,
        score: prev.score + 1,
        state: 'won',
      }))
    }
  }

  useEffect(() => {
    if (game.state === 'playing' && game.timeRemaining > 0) {
      const timer = setTimeout(() => {
        setGame((prev) => ({ ...prev, timeRemaining: prev.timeRemaining - 1 }))
      }, 1000)
      return () => clearTimeout(timer)
    } else if (game.state === 'playing' && game.timeRemaining === 0) {
      setGame((prev) => ({ ...prev, state: 'lost' }))
    }
  }, [game.state, game.timeRemaining])

  useEffect(() => {
    if (game.state === 'won') {
      const timer = setTimeout(() => {
        nextRound()
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [game.state])

  return (
    <PageContainer>
      <PageHeader title="Piano Note Tester" />

      <div>
        {/* Score Display */}
        {game.state !== 'setup' && game.state !== 'finished' && (
          <div className={styles.scoreCard}>
            <div className={styles.scoreGrid}>
              <div className={styles.scoreItem}>
                <GovUKTypography variant="body-s" marginBottom={1}>
                  Round
                </GovUKTypography>
                <div className={styles.scoreNumber}>
                  {game.currentRound}{' '}
                  <span style={{ fontSize: '20px', color: '#505a5f' }}>
                    / {game.totalRounds}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Setup Phase */}
        {game.state === 'setup' && (
          <div>
            <GovUKTypography variant="heading-m">Game Settings</GovUKTypography>
            <div
              style={{
                display: 'flex',
                gap: '20px',
                flexWrap: 'wrap',
                justifyContent: 'center',
                maxWidth: '600px',
                margin: '0 auto',
              }}
            >
              <div style={{ flex: '1', minWidth: '200px' }}>
                <GovUKFormGroup label="🎯 Rounds" htmlFor="rounds" labelSize="s">
                  <GovUKInput
                    type="number"
                    value={game.totalRounds}
                    onChange={(e) =>
                      setGame((prev) => ({
                        ...prev,
                        totalRounds: Math.max(1, parseInt(e.target.value) || 5),
                      }))
                    }
                    min={1}
                    max={20}
                    id="rounds"
                  />
                </GovUKFormGroup>
              </div>
              <div style={{ flex: '1', minWidth: '200px' }}>
                <GovUKFormGroup
                  label="⏱️ Time (seconds)"
                  htmlFor="timer"
                  labelSize="s"
                >
                  <GovUKInput
                    type="number"
                    value={game.timerDuration}
                    onChange={(e) =>
                      setGame((prev) => ({
                        ...prev,
                        timerDuration: Math.max(
                          1,
                          parseInt(e.target.value) || 10,
                        ),
                      }))
                    }
                    min={1}
                    max={60}
                    id="timer"
                  />
                </GovUKFormGroup>
              </div>
            </div>
            <div
              style={{
                maxWidth: '600px',
                margin: '20px auto 0',
                textAlign: 'center',
              }}
            >
              <GovUKCheckbox
                label="🎵 Show note labels on keys"
                checked={game.showNotes}
                onChange={(e) =>
                  setGame((prev) => ({
                    ...prev,
                    showNotes: e.target.checked,
                  }))
                }
                id="show-notes"
              />
            </div>
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <GovUKButton onClick={startGame}>▶️ Start Game</GovUKButton>
            </div>
          </div>
        )}

        {/* Playing Phase */}
        {game.state === 'playing' && (
          <div className={styles.noteDisplay}>
            <div className={styles.noteLetter}>{game.targetNote}</div>
            <div
              className={
                game.timeRemaining <= 3
                  ? `${styles.timer} ${styles.timerUrgent}`
                  : `${styles.timer} ${styles.timerNormal}`
              }
            >
              ⏱️ {game.timeRemaining}s
            </div>
          </div>
        )}

        {/* Won Phase */}
        {game.state === 'won' && (
          <div className={`${styles.resultCard} ${styles.resultGreen}`}>
            <div className={styles.resultContent}>
              <div className={styles.resultIcon}>✅</div>
              <div
                className={styles.noteLetter}
                style={{ fontSize: '60px', color: '#00703c' }}
              >
                {game.targetNote}
              </div>
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '20px',
                    color: '#00703c',
                    marginBottom: '4px',
                  }}
                >
                  Correct!
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lost Phase */}
        {game.state === 'lost' && (
          <div>
            <div className={`${styles.resultCard} ${styles.resultRed}`}>
              <div className={styles.resultContent}>
                <div className={styles.resultIcon}>❌</div>
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '20px',
                      color: '#d4351c',
                      marginBottom: '4px',
                    }}
                  >
                    Time's Up!
                  </div>
                  <GovUKTypography variant="body-s" marginBottom={0}>
                    Answer:{' '}
                    <strong style={{ fontSize: '32px' }}>
                      {game.targetNote}
                    </strong>
                  </GovUKTypography>
                </div>
              </div>
            </div>
            <div className={styles.buttonGroup}>
              <GovUKButton onClick={startGame}>🔄 Try Again</GovUKButton>
              <GovUKButton onClick={resetGame} variant="secondary">
                ⚙️ Settings
              </GovUKButton>
            </div>
          </div>
        )}

        {/* Finished Phase */}
        {game.state === 'finished' && (
          <div>
            <div className={`${styles.resultCard} ${styles.resultPurple}`}>
              <div className={styles.resultContent}>
                <div className={styles.resultIcon}>
                  {game.score === game.totalRounds && '🏆'}
                  {game.score >= game.totalRounds * 0.8 &&
                    game.score < game.totalRounds &&
                    '⭐'}
                  {game.score >= game.totalRounds * 0.5 &&
                    game.score < game.totalRounds * 0.8 &&
                    '👍'}
                  {game.score < game.totalRounds * 0.5 && '💪'}
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '20px',
                      color: '#4c2c92',
                      marginBottom: '4px',
                    }}
                  >
                    Game Complete!
                  </div>
                  <GovUKTag color="purple">
                    {game.score === game.totalRounds && 'Perfect!'}
                    {game.score >= game.totalRounds * 0.8 &&
                      game.score < game.totalRounds &&
                      'Great!'}
                    {game.score >= game.totalRounds * 0.5 &&
                      game.score < game.totalRounds * 0.8 &&
                      'Good!'}
                    {game.score < game.totalRounds * 0.5 && 'Keep Going!'}
                  </GovUKTag>
                </div>
                <div>
                  <div
                    className={styles.resultScore}
                    style={{ color: '#4c2c92' }}
                  >
                    {game.score}
                    <span style={{ fontSize: '28px', color: '#505a5f' }}>
                      /{game.totalRounds}
                    </span>
                  </div>
                  <GovUKTypography variant="body-l" marginBottom={0}>
                    {Math.round((game.score / game.totalRounds) * 100)}%
                  </GovUKTypography>
                </div>
              </div>
            </div>
            <div className={styles.buttonGroup}>
              <GovUKButton onClick={startGame}>🔄 Play Again</GovUKButton>
              <GovUKButton onClick={resetGame} variant="secondary">
                ⚙️ Settings
              </GovUKButton>
            </div>
          </div>
        )}

        {/* Piano Keyboard */}
        {(game.state === 'playing' ||
          game.state === 'won' ||
          game.state === 'lost') && (
          <div className={styles.keyboardContainer}>
            <div
              style={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '18px',
                marginBottom: '15px',
              }}
            >
              🎹 Tap a key
            </div>
            <div
              style={{
                pointerEvents: game.state === 'playing' ? 'auto' : 'none',
                opacity: game.state === 'playing' ? 1 : 0.6,
              }}
            >
              <SimplePiano
                onNoteClick={onPlayNote}
                disabled={game.state !== 'playing'}
                showNotes={game.showNotes}
              />
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  )
}
